import { env } from '@/lib/env'
import { trackException } from '@/lib/telemetry'
import {
  BrowserAuthError,
  BrowserAuthErrorCodes,
  BrowserCacheLocation,
  Configuration,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'

export const msalConfig: Configuration = {
  auth: {
    clientId: env.VITE_CLIENT_ID,
    authority: 'https://login.microsoftonline.com/' + env.VITE_TENANT_ID,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    // Shared by every tab, so a quote opened from Dynamics in a new tab is
    // already signed in instead of round-tripping through Entra. MSAL v5
    // encrypts it with a key held in a browser-session cookie, so tokens are
    // unreadable once the browser closes.
    cacheLocation: BrowserCacheLocation.LocalStorage,
  },
  system: {
    loggerOptions: {
      logLevel: import.meta.env.DEV ? LogLevel.Info : LogLevel.Warning,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return
        }
        switch (level) {
          case LogLevel.Error:
            console.error('MSAL ERROR:', message)
            return
          case LogLevel.Info:
            console.info('MSAL INFO:', message)
            return
          case LogLevel.Verbose:
            console.debug('MSAL VERBOSE:', message)
            return
          case LogLevel.Warning:
            console.warn('MSAL WARNING:', message)
            return
        }
      },
    },
  },
}

export const tokenScopes = [`${env.VITE_DYNAMICS_API_BASE_URL}/.default`]

/**
 * Silent renewal's hidden iframe lands on this bridge page, which hands the
 * response back to the app (MSAL v5 waits for it and times out without it).
 */
export const silentRedirectUri = `${window.location.origin}/redirect.html`

export const msalInstance = new PublicClientApplication(msalConfig)

export function getIsAuthenticated() {
  return (
    !!msalInstance.getActiveAccount() ||
    msalInstance.getAllAccounts().length > 0 ||
    !!msalInstance.getAccount({ tenantId: env.VITE_TENANT_ID })
  )
}

export function useIsAuthenticated() {
  const { instance: msalInstance } = useMsal()
  return (
    !!msalInstance.getActiveAccount() ||
    msalInstance.getAllAccounts().length > 0 ||
    !!msalInstance.getAccount({ tenantId: env.VITE_TENANT_ID })
  )
}

/**
 * A same-origin path to navigate to, or null. Resolved with the URL parser so
 * every form a browser treats as external ("//evil.com", "/\evil.com") is
 * refused: the deep link round-trips through the OAuth state param.
 */
export function safeInternalPath(target: unknown): string | null {
  if (typeof target !== 'string' || !target.startsWith('/')) return null
  try {
    const url = new URL(target, window.location.origin)
    // No hash: a quote URL never needs one, and a hash could carry a second,
    // forged sign-in response into the return.
    return url.origin === window.location.origin ? url.pathname + url.search : null
  } catch {
    return null
  }
}

/**
 * Where to land after an interactive sign-in: the quote URL at the moment the
 * sign-in starts. It rides the OAuth state param through Entra, so it survives
 * MSAL's sessionStorage request cache being lost.
 */
export function deepLinkState() {
  return safeInternalPath(window.location.pathname + window.location.search) ?? '/'
}

/**
 * The quote URL a sign-in response (`#code=…&state=…`) carries, read straight
 * off the URL so it survives MSAL failing to handle the response. MSAL's state
 * is `<library state>|<deepLinkState()>`.
 */
export function responseDeepLink(hash: string): string | null {
  const params = new URLSearchParams(hash.replace(/^#/, ''))
  if (!params.has('code') && !params.has('error')) return null
  const state = params.get('state') ?? ''
  const separator = state.indexOf('|')
  return separator === -1 ? null : safeInternalPath(state.slice(separator + 1))
}

/**
 * A sign-in return that keeps failing (Entra refuses the user, the fallback
 * window is spent): there is no quote to render, so the page shows why and
 * offers a fresh start at the quote the sign-in began from.
 */
export class SignInReturnError extends Error {
  readonly target: string

  constructor(message: string, target: string) {
    super(message)
    this.name = 'SignInReturnError'
    this.target = target
  }
}

const AUTH_FALLBACK_AT_KEY = 'cpq-auth-fallback-at'
const AUTH_FALLBACK_WINDOW_MS = 30_000

/**
 * One fallback restore per 30 s, so a sign-in that keeps failing can't loop
 * through Entra. Refuses when storage is unavailable.
 */
function claimAuthFallback() {
  try {
    const lastAt = Number(sessionStorage.getItem(AUTH_FALLBACK_AT_KEY)) || 0
    if (Date.now() - lastAt < AUTH_FALLBACK_WINDOW_MS) return false
    sessionStorage.setItem(AUTH_FALLBACK_AT_KEY, String(Date.now()))
    return true
  } catch {
    return false
  }
}

export function isInteractionInProgress(error: unknown) {
  return error instanceof BrowserAuthError && error.errorCode === BrowserAuthErrorCodes.interactionInProgress
}

/** The only first sign-in path: a full-page redirect that returns to this quote. */
export async function redirectToLogin() {
  try {
    await msalInstance.loginRedirect({ scopes: tokenScopes, state: deepLinkState() })
  } catch (error) {
    // Another sign-in already started: the page is leaving.
    if (!isInteractionInProgress(error)) throw error
  }
}

/**
 * Starts MSAL and handles a sign-in return before the app renders. Returns true
 * while the page navigates to the quote the sign-in started from; the caller
 * stops booting.
 */
export async function initAuth(): Promise<boolean> {
  // Read before MSAL clears the response from the URL.
  const responseTarget = responseDeepLink(window.location.hash)
  let target: string | null = null
  let failure: unknown
  try {
    await msalInstance.initialize()
    // Handle the response here rather than letting MSAL navigate to its cached
    // start URL: the deep link comes back in the state param.
    const result = await msalInstance.handleRedirectPromise({ navigateToLoginRequestUrl: false })
    if (result?.account) msalInstance.setActiveAccount(result.account)
    target = safeInternalPath(result?.state)
  } catch (error) {
    failure = error
    trackException(error, { context: 'auth.initAuth' })
  }
  // MSAL drops the response when its sessionStorage request cache is gone (a
  // discarded tab) or redemption fails, leaving the page on "/" with no quote.
  // Go back to the quote anyway; the builder signs in again if it must.
  if (!target && responseTarget) {
    if (!claimAuthFallback()) {
      // Only MSAL's own error, never text from the URL: a crafted link could
      // otherwise put any message on this page.
      const reason = failure instanceof Error ? failure.message : 'Sign-in did not complete.'
      throw new SignInReturnError(reason, responseTarget)
    }
    target = responseTarget
  }
  // After a failed initialize the account cache can throw on read; boot on
  // without an account rather than stall on the loader.
  try {
    if (!msalInstance.getActiveAccount()) {
      const account = msalInstance.getAllAccounts().at(0)
      if (account) msalInstance.setActiveAccount(account)
    }
  } catch (error) {
    trackException(error, { context: 'auth.initAuth.account' })
  }
  if (target && target !== window.location.pathname + window.location.search) {
    window.location.replace(target)
    return true
  }
  return false
}
